import React, { useState, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, RadioGroup, FormControlLabel, Radio, IconButton, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import { useDropzone } from "react-dropzone";
import { parseUploadFile, groupByAssetType } from "./utils/fileParser";
import { validateRows } from "./utils/bulkValidation";
import { downloadTemplate } from "./utils/templateGenerator";
import BulkUploadPreview from "./BulkUploadPreview";
import "./BulkUploadDialog.scss";

const ACCEPTED_TYPES = {
  "text/csv": [".csv"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
};

const generateId = () => Date.now() + Math.random().toString(36).substr(2, 9);

const BulkUploadDialog = ({ open, onClose, onApply }) => {
  const [parsedRows, setParsedRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [fileName, setFileName] = useState("");
  const [mergeMode, setMergeMode] = useState("append");
  const [step, setStep] = useState("upload");

  const resetState = () => {
    setParsedRows([]);
    setValidationErrors([]);
    setFileErrors([]);
    setFileName("");
    setStep("upload");
    setMergeMode("append");
  };

  const handleClose = () => { resetState(); onClose(); };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!acceptedFiles.length) return;
    const file = acceptedFiles[0];
    setFileName(file.name);
    setFileErrors([]);

    const { rows, errors } = await parseUploadFile(file);
    if (errors.length) {
      setFileErrors(errors);
      setParsedRows([]);
      setValidationErrors([]);
      return;
    }

    const rowErrors = validateRows(rows);
    setParsedRows(rows);
    setValidationErrors(rowErrors);
    setStep("preview");
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: ACCEPTED_TYPES, maxFiles: 1, multiple: false,
  });

  const handleApply = () => {
    const validRows = parsedRows.filter((_, idx) => validationErrors[idx] === null);
    const grouped = groupByAssetType(validRows);

    const assets = {
      finalURL: grouped.final_url.length ? grouped.final_url[grouped.final_url.length - 1].text : null,
      businessName: grouped.business_name.length ? grouped.business_name[grouped.business_name.length - 1].text : null,
      headlines: grouped.headline.map((r) => ({ id: generateId(), text: r.text, isPinned: false })),
      descriptions: grouped.description.map((r) => ({ id: generateId(), text: r.text })),
      siteLinks: grouped.sitelink.map((r) => ({
        id: generateId(), sitelinkText: r.sitelinkText,
        description1: r.description1, description2: r.description2, finalURL: r.finalURL,
      })),
      callOuts: grouped.callout.map((r) => ({ id: generateId(), text: r.text })),
      structuredSnippets: grouped.structured_snippet.map((r) => ({ id: generateId(), text: r.text })),
      displayPaths: grouped.display_path.map((r) => ({
        id: r.pathId || generateId(), text: r.text, irremovable: !!r.pathId,
      })),
      calls: grouped.phone_call.map((r) => ({
        id: generateId(), phoneNumber: r.phoneNumber, countryCode: r.countryCode,
      })),
    };

    onApply(assets, mergeMode);
    handleClose();
  };

  const validCount = validationErrors.filter((e) => e === null).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth className="bulk-upload-dialog">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "16px", fontWeight: 600, borderBottom: "1px solid #e0e0e0", padding: "12px 24px" }}>
        Upload Ad Creatives
        <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "24px", minHeight: 300 }}>
        {step === "upload" && (
          <Box>
            <Box mb={3} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontFamily: "Inter", color: "#666" }}>Need the template?</Typography>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadTemplate("xlsx")}
                sx={{ fontFamily: "Inter", fontSize: "12px", textTransform: "none", color: "#1976d2" }}>ad_creatives_template.xlsx</Button>
              <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadTemplate("csv")}
                sx={{ fontFamily: "Inter", fontSize: "12px", textTransform: "none", color: "#1976d2" }}>ad_creatives_template.csv</Button>
            </Box>
            <Box {...getRootProps()} className={`dropzone ${isDragActive ? "dropzone-active" : ""}`}>
              <input {...getInputProps()} />
              <CloudUploadIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
              <Typography variant="body1" sx={{ fontFamily: "Inter", fontWeight: 500, color: "#444" }}>
                {isDragActive ? "Drop the file here..." : "Drag & drop a CSV or XLSX file here"}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: "Inter", color: "#999", mt: 0.5 }}>or click to browse</Typography>
            </Box>
            {fileErrors.map((err, idx) => (
              <Alert key={idx} severity="error" sx={{ mt: 2, fontFamily: "Inter" }}>{err}</Alert>
            ))}
          </Box>
        )}

        {step === "preview" && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="body2" sx={{ fontFamily: "Inter", color: "#666" }}>
                File: <strong>{fileName}</strong>
              </Typography>
              <Button size="small" onClick={() => { setStep("upload"); setParsedRows([]); setValidationErrors([]); setFileName(""); }}
                sx={{ fontFamily: "Inter", fontSize: "12px", textTransform: "none" }}>Upload Different File</Button>
            </Box>
            <BulkUploadPreview rows={parsedRows} validationErrors={validationErrors} />
            <Box mt={2} p={2} sx={{ backgroundColor: "#f9f9f9", borderRadius: "6px" }}>
              <Typography variant="body2" sx={{ fontFamily: "Inter", fontWeight: 600, mb: 1 }}>
                How should uploaded assets be applied?
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "Inter", color: "#888", display: "block", mb: 1 }}>
                Note: Final URL, Business Name, Display Path and Calls always replace existing values.
              </Typography>
              <RadioGroup value={mergeMode} onChange={(e) => setMergeMode(e.target.value)} row>
                <FormControlLabel value="append" control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontFamily: "Inter" }}>Append to existing creatives</Typography>} />
                <FormControlLabel value="replace" control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontFamily: "Inter" }}>Replace existing creatives</Typography>} />
              </RadioGroup>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px", borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={handleClose} sx={{ fontFamily: "Inter", textTransform: "none", color: "#666" }}>Cancel</Button>
        {step === "preview" && (
          <Button onClick={handleApply} variant="contained" disabled={validCount === 0}
            sx={{ fontFamily: "Inter", textTransform: "none", backgroundColor: "#61D5A9", "&:hover": { backgroundColor: "#4ec49a" } }}>
            Apply {validCount} Valid Asset{validCount !== 1 ? "s" : ""}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadDialog;